import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Award, Calculator, Eye, Loader2, Pencil, Play, Plus, RotateCcw, Sparkles, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Event } from '../../services/events';
import type { PrizeType } from '../../services/prizes';
import {
  AWARDS_ALREADY_REVEALED,
  AWARDS_ALREADY_STARTED,
  AWARDS_NOTHING_TO_REVEAL,
  AWARDS_PRIZE_FAILED,
  announceAwardNames,
  loadAwardInputs,
  resetEventAwards,
  revealNextAward,
  saveAwardDraft,
  startAwardReveal,
  subscribeToAwardDraft,
  subscribeToEventAwards,
  type AwardInputs,
} from '../../services/eventAwards';
import {
  AWARD_DEFINITIONS,
  CUSTOM_AWARD_NAME_MAX,
  CUSTOM_AWARD_TAGLINE_MAX,
  DEFAULT_CUSTOM_AWARD_EMOJI,
  blankPick,
  buildAttendeeOptions,
  computeAwards,
  isPredefinedAwardId,
  nextCustomCategoryId,
  revealablePicks,
  type AwardDraft,
  type AwardPick,
  type EventAwards,
} from '../../utils/eventAwards';
import { PRIZE_OPTIONS, prizeNeedsText } from './prizeOptions';
import EventAwardsCard from './EventAwardsCard';

interface EventAwardsPanelProps {
  eventId: string;
  djId: string;
  event: Event;
}

type Confirming = 'start' | 'reset' | null;

const NO_WINNER = '__no-winner__';
const KNOWN_REFUSALS: ReadonlySet<string> = new Set([AWARDS_ALREADY_STARTED, AWARDS_NOTHING_TO_REVEAL]);

const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm';
const rowClass = 'px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50';

function defaultPicks(isRanked: boolean): AwardPick[] {
  return AWARD_DEFINITIONS.filter((definition) => isRanked || !definition.rankedOnly).map(blankPick);
}

function sortPicks(picks: AwardPick[]): AwardPick[] {
  const order = (pick: AwardPick) => {
    const index = AWARD_DEFINITIONS.findIndex((definition) => definition.id === pick.categoryId);
    return index === -1 ? AWARD_DEFINITIONS.length : index;
  };
  return picks
    .map((pick, position) => ({ pick, position }))
    .sort((a, b) => order(a.pick) - order(b.pick) || a.position - b.position)
    .map(({ pick }) => pick);
}

function prizeLabel(pick: AwardPick): string | null {
  if (!pick.prizeType) return null;
  const option = PRIZE_OPTIONS.find((candidate) => candidate.type === pick.prizeType);
  const label = option ? `${option.icon} ${option.label}` : pick.prizeType;
  return pick.prizeCustomText ? `${label}: ${pick.prizeCustomText}` : label;
}

export default function EventAwardsPanel({ eventId, djId, event }: EventAwardsPanelProps) {
  const isRanked = event.settings?.queueMode === 'ranked';
  const [draft, setDraft] = useState<AwardDraft | null | undefined>(undefined);
  const [awards, setAwards] = useState<EventAwards | null | undefined>(undefined);
  const [picks, setPicks] = useState<AwardPick[]>(() => defaultPicks(isRanked));
  const [showNamesEarly, setShowNamesEarly] = useState(false);
  const [inputs, setInputs] = useState<AwardInputs | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [customName, setCustomName] = useState('');
  const [customEmoji, setCustomEmoji] = useState(DEFAULT_CUSTOM_AWARD_EMOJI);
  const [customTagline, setCustomTagline] = useState('');

  useEffect(() => {
    const unsubscribeDraft = subscribeToAwardDraft(eventId, setDraft);
    const unsubscribeAwards = subscribeToEventAwards(eventId, setAwards);
    return () => {
      unsubscribeDraft();
      unsubscribeAwards();
    };
  }, [eventId]);

  useEffect(() => {
    if (draft === undefined) return;
    setPicks(draft ? draft.picks : defaultPicks(isRanked));
    setShowNamesEarly(draft?.showNamesEarly ?? false);
  }, [draft, isRanked]);

  const computedAt = draft?.computedAt ?? null;
  const stage = awards?.status === 'revealing' || awards?.status === 'done'
    ? 'reveal'
    : computedAt ? 'winners' : 'setup';

  useEffect(() => {
    if (stage === 'setup' || inputs) return;
    let cancelled = false;
    loadAwardInputs(eventId)
      .then((loaded) => { if (!cancelled) setInputs(loaded); })
      .catch((error) => {
        console.error('[EventAwards] Loading attendees failed:', error);
        toast.error("Couldn't load the night's attendees", { id: 'awards-load-error' });
      });
    return () => { cancelled = true; };
  }, [stage, inputs, eventId]);

  const attendeeOptions = useMemo(
    () => inputs ? buildAttendeeOptions(inputs.requests, inputs.votes, inputs.rankingAttendeeIds, djId) : [],
    [inputs, djId],
  );

  const persist = async (nextPicks: AwardPick[], patch: { showNamesEarly?: boolean; computedAt?: string | null } = {}) => {
    setPicks(nextPicks);
    if (patch.showNamesEarly !== undefined) setShowNamesEarly(patch.showNamesEarly);
    try {
      await saveAwardDraft(eventId, djId, {
        picks: nextPicks,
        showNamesEarly: patch.showNamesEarly ?? showNamesEarly,
        computedAt: patch.computedAt !== undefined ? patch.computedAt : computedAt,
      });
    } catch (error) {
      console.error('[EventAwards] Saving the awards failed:', error);
      toast.error("Couldn't save the awards", { id: 'awards-save-error' });
    }
  };

  const updatePick = (categoryId: string, patch: Partial<AwardPick>) => {
    void persist(picks.map((pick) => pick.categoryId === categoryId ? { ...pick, ...patch } : pick));
  };

  const toggleCategory = (categoryId: string) => {
    const selected = picks.some((pick) => pick.categoryId === categoryId);
    const definition = AWARD_DEFINITIONS.find((candidate) => candidate.id === categoryId);
    if (!definition) return;
    void persist(selected
      ? picks.filter((pick) => pick.categoryId !== categoryId)
      : sortPicks([...picks, blankPick(definition)]));
  };

  const addCustomAward = () => {
    const name = customName.trim().slice(0, CUSTOM_AWARD_NAME_MAX);
    if (!name) return;
    const custom: AwardPick = {
      ...blankPick(AWARD_DEFINITIONS[0]),
      categoryId: nextCustomCategoryId(picks),
      name,
      emoji: customEmoji.trim() || DEFAULT_CUSTOM_AWARD_EMOJI,
      tagline: customTagline.trim().slice(0, CUSTOM_AWARD_TAGLINE_MAX),
      custom: true,
    };
    setCustomName('');
    setCustomEmoji(DEFAULT_CUSTOM_AWARD_EMOJI);
    setCustomTagline('');
    void persist([...picks, custom]);
  };

  const changeWinner = (categoryId: string, winnerId: string) => {
    const option = attendeeOptions.find((candidate) => candidate.id === winnerId);
    if (!option && winnerId !== NO_WINNER) return;
    updatePick(categoryId, {
      winnerId: option ? option.id : null,
      winnerName: option ? option.name : null,
      stat: null,
      overridden: true,
    });
  };

  const workOutWinners = async () => {
    setBusy('compute');
    try {
      const loaded = await loadAwardInputs(eventId);
      setInputs(loaded);
      const computed = computeAwards({
        selectedIds: picks.map((pick) => pick.categoryId).filter(isPredefinedAwardId),
        customAwards: picks.filter((pick) => pick.custom),
        ...loaded,
        isRanked,
        djId,
        existing: picks,
      });
      await persist(computed, { computedAt: new Date().toISOString() });
    } catch (error) {
      console.error('[EventAwards] Working out winners failed:', error);
      toast.error("Couldn't work out the winners", { id: 'awards-compute-error' });
    } finally {
      setBusy(null);
    }
  };

  const runAction = async (key: string, action: () => Promise<unknown>, failure: string) => {
    setBusy(key);
    try {
      await action();
    } catch (error) {
      console.error(`[EventAwards] ${key} failed:`, error);
      toast.error(error instanceof Error && KNOWN_REFUSALS.has(error.message) ? error.message : failure, { id: `awards-${key}-error` });
    } finally {
      setBusy(null);
    }
  };

  const handleRevealNext = async () => {
    if (!awards || busy) return;
    setBusy('reveal');
    try {
      const revealed = await revealNextAward(eventId, djId, awards.revealed.length);
      toast.success(`${revealed.emoji} ${revealed.name}: ${revealed.winnerName}`, { id: 'awards-reveal-success' });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === AWARDS_ALREADY_REVEALED || message === AWARDS_PRIZE_FAILED) {
        toast.error(message, { id: 'awards-reveal-error' });
      } else {
        console.error('[EventAwards] Reveal failed:', error);
        toast.error("Couldn't reveal the next award", { id: 'awards-reveal-error' });
      }
    } finally {
      setBusy(null);
    }
  };

  const handleReset = async () => {
    setConfirming(null);
    await runAction('reset', async () => {
      await resetEventAwards(eventId);
      setInputs(null);
      toast.success('Awards cleared', { id: 'awards-reset-success' });
    }, "Couldn't clear the awards");
  };

  const handleStart = async () => {
    setConfirming(null);
    await runAction('start', () => startAwardReveal(eventId, djId), "Couldn't start the awards");
  };

  if (draft === undefined || awards === undefined) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const revealable = revealablePicks(picks);
  const confirmBox = (message: string, onConfirm: () => void, label: string) => (
    <div className="p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 space-y-2">
      <p className="text-sm text-gray-800 dark:text-gray-200">{message}</p>
      <div className="flex gap-2">
        <button onClick={onConfirm} className="px-3 py-1.5 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 font-medium">{label}</button>
        <button onClick={() => setConfirming(null)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300">Cancel</button>
      </div>
    </div>
  );

  const prizeControls = (pick: AwardPick) => (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <label className="text-xs text-gray-500 dark:text-gray-400" htmlFor={`award-prize-${pick.categoryId}`}>Prize</label>
      <select
        id={`award-prize-${pick.categoryId}`}
        value={pick.prizeType ?? ''}
        onChange={(changeEvent) => {
          const prizeType = (changeEvent.target.value || null) as PrizeType | null;
          updatePick(pick.categoryId, { prizeType, prizeCustomText: prizeType && prizeNeedsText(prizeType) ? pick.prizeCustomText : null });
        }}
        className="px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
      >
        <option value="">None</option>
        {PRIZE_OPTIONS.map((option) => (
          <option key={option.type} value={option.type}>{option.icon} {option.label}</option>
        ))}
      </select>
      {pick.prizeType && prizeNeedsText(pick.prizeType) && (
        <input
          key={`${pick.categoryId}-${pick.prizeCustomText ?? ''}`}
          type="text"
          aria-label={`${pick.name} prize text`}
          defaultValue={pick.prizeCustomText ?? ''}
          onBlur={(changeEvent) => {
            const text = changeEvent.target.value.trim() || null;
            if (text !== pick.prizeCustomText) updatePick(pick.categoryId, { prizeCustomText: text });
          }}
          placeholder={pick.prizeType === 'shoutout' ? 'Shoutout message' : 'Prize description'}
          className="flex-1 min-w-[8rem] px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
        />
      )}
    </div>
  );

  const winnerSelect = (pick: AwardPick, allowNone: boolean) => (
    <select
      aria-label={`Change winner of ${pick.name}`}
      value=""
      onChange={(changeEvent) => changeWinner(pick.categoryId, changeEvent.target.value)}
      className="mt-1 w-full px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
    >
      <option value="" disabled>{pick.winnerId ? 'Change winner…' : 'Pick a winner…'}</option>
      {allowNone && pick.winnerId && <option value={NO_WINNER}>No winner</option>}
      {attendeeOptions.filter((option) => option.id !== pick.winnerId).map((option) => (
        <option key={option.id} value={option.id}>{option.name}</option>
      ))}
    </select>
  );

  const resetButton = (
    <button
      onClick={() => setConfirming('reset')}
      disabled={busy !== null}
      className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
    >
      <RotateCcw className="w-3 h-3" />
      Start over
    </button>
  );

  if (stage === 'setup') {
    const customPicks = picks.filter((pick) => pick.custom);
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Award categories</h3>
          {AWARD_DEFINITIONS.map((definition) => {
            const pick = picks.find((candidate) => candidate.categoryId === definition.id);
            const disabled = definition.rankedOnly && !isRanked;
            return (
              <div key={definition.id} className={`${rowClass} ${disabled ? 'opacity-50' : ''}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(pick) && !disabled}
                    disabled={disabled}
                    onChange={() => toggleCategory(definition.id)}
                    className="mt-1"
                  />
                  <span className="flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {definition.emoji} {definition.name}
                      {disabled && <span className="ml-1 text-xs font-normal text-gray-500">(ranked queue only)</span>}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{definition.tagline}</span>
                  </span>
                </label>
                {pick && !disabled && prizeControls(pick)}
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom awards</h3>
          {customPicks.map((pick) => (
            <div key={pick.categoryId} className={rowClass}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{pick.emoji} {pick.name}</p>
                  {pick.tagline && <p className="text-xs text-gray-500 dark:text-gray-400">{pick.tagline}</p>}
                </div>
                <button
                  onClick={() => void persist(picks.filter((candidate) => candidate.categoryId !== pick.categoryId))}
                  aria-label={`Remove ${pick.name}`}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <Trash2 className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              {prizeControls(pick)}
            </div>
          ))}
          <div className="space-y-2 p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
            <div className="flex gap-2">
              <input
                aria-label="Custom award emoji"
                value={customEmoji}
                onChange={(changeEvent) => setCustomEmoji(changeEvent.target.value)}
                maxLength={8}
                className={`${inputClass} w-16 text-center`}
              />
              <input
                aria-label="Custom award name"
                value={customName}
                onChange={(changeEvent) => setCustomName(changeEvent.target.value)}
                maxLength={CUSTOM_AWARD_NAME_MAX}
                placeholder="Award name, e.g. Best Dancer"
                className={inputClass}
              />
            </div>
            <input
              aria-label="Custom award tagline"
              value={customTagline}
              onChange={(changeEvent) => setCustomTagline(changeEvent.target.value)}
              maxLength={CUSTOM_AWARD_TAGLINE_MAX}
              placeholder="Tagline (optional)"
              className={inputClass}
            />
            <button
              onClick={addCustomAward}
              disabled={!customName.trim()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add custom award
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={showNamesEarly}
            onChange={() => void persist(picks, { showNamesEarly: !showNamesEarly })}
          />
          Show award names on phones before the reveal
        </label>

        <button
          onClick={workOutWinners}
          disabled={busy !== null || picks.length === 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
        >
          {busy === 'compute' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
          Work out winners
        </button>

        {(draft || awards) && (confirming === 'reset'
          ? confirmBox('This clears the awards on every phone.', handleReset, 'Clear awards')
          : resetButton)}
      </div>
    );
  }

  if (stage === 'winners') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Worked out at {format(new Date(computedAt!), 'h:mm a')}</span>
          <div className="flex gap-3">
            <button onClick={workOutWinners} disabled={busy !== null} className="flex items-center gap-1 text-primary hover:underline disabled:opacity-50">
              {busy === 'compute' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Recompute
            </button>
            <button onClick={() => void persist(picks, { computedAt: null })} disabled={busy !== null} className="flex items-center gap-1 text-primary hover:underline disabled:opacity-50">
              <Pencil className="w-3 h-3" />
              Edit awards
            </button>
          </div>
        </div>

        {awards?.status === 'announced' && (
          <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
            <Eye className="w-3 h-3" />
            Award names are showing on phones
          </p>
        )}

        <div className="space-y-2">
          {picks.map((pick) => (
            <div key={pick.categoryId} className={rowClass}>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{pick.emoji} {pick.name}</p>
              {pick.winnerId ? (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {pick.winnerName}
                  {pick.stat && <span className="text-xs text-gray-500 dark:text-gray-400"> · {pick.stat}</span>}
                </p>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {pick.custom ? 'Pick a winner' : 'No winner tonight'}
                  <span className="text-xs"> · skipped at the reveal</span>
                </p>
              )}
              {prizeLabel(pick) && <p className="text-xs text-amber-700 dark:text-amber-300">Prize: {prizeLabel(pick)}</p>}
              {winnerSelect(pick, true)}
            </div>
          ))}
        </div>

        {showNamesEarly && !awards && (
          <button
            onClick={() => runAction('announce', () => announceAwardNames(eventId, djId, picks), "Couldn't show the names")}
            disabled={busy !== null}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50 text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            Show names on phones now
          </button>
        )}

        {confirming === 'start'
          ? confirmBox('Phones will get a push and the first award is one tap away.', handleStart, 'Start the awards')
          : (
            <button
              onClick={() => setConfirming('start')}
              disabled={busy !== null || revealable.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium"
            >
              <Play className="w-4 h-4" />
              Start the awards ({revealable.length})
            </button>
          )}

        {confirming === 'reset'
          ? confirmBox('This clears the awards on every phone.', handleReset, 'Clear awards')
          : resetButton}
      </div>
    );
  }

  const revealedCount = awards!.revealed.length;
  const remaining = revealable.slice(revealedCount);
  const next = remaining[0] ?? null;
  const done = awards!.status === 'done';

  return (
    <div className="space-y-4">
      {done ? (
        <p className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
          <Sparkles className="w-4 h-4" />
          All {awards!.totalCount} awards revealed
        </p>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Award {revealedCount + 1} of {awards!.totalCount}
          </p>
          {next && (
            <div className="p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
              <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300">Next up (only you can see this)</p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">{next.emoji} {next.name}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{next.winnerName}{next.stat ? ` · ${next.stat}` : ''}</p>
              {prizeLabel(next) && <p className="text-xs text-amber-700 dark:text-amber-300">Prize: {prizeLabel(next)}</p>}
            </div>
          )}
          <button
            onClick={handleRevealNext}
            disabled={busy !== null || !next}
            className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-amber-500 text-white text-lg rounded-xl hover:bg-amber-600 disabled:opacity-50 font-bold"
          >
            {busy === 'reveal' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Award className="w-5 h-5" />}
            Reveal next
          </button>
          {remaining.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Still to reveal</h3>
              {remaining.map((pick) => (
                <div key={pick.categoryId} className={rowClass}>
                  <p className="text-sm text-gray-900 dark:text-white">{pick.emoji} {pick.name}: {pick.winnerName}</p>
                  {winnerSelect(pick, false)}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <EventAwardsCard awards={awards!} />

      {confirming === 'reset'
        ? confirmBox('This clears the awards on every phone.', handleReset, 'Clear awards')
        : resetButton}
    </div>
  );
}
